import {SelectProvider, useSelectContext} from '@ariakit/react'
import {Flexbox} from 'src/components/flexbox/Flexbox'
import {SelectItem, SelectList, SelectPopover, SelectTrigger} from 'src/components/select-atoms/SelectAtoms'
import {Spinner} from 'src/components/spinner/Spinner'
import {InputHelpText} from 'src/components/input-help-text/InputHelpText'
import {useTranslation} from 'react-i18next'
import {AdminHostApprovalTaskImage, ROOM_ACCESSIBILITY_FEATURES_MAP} from 'src/features/admin-host-approval/types'
import {useAccessibilityFeatures} from 'src/features/admin-host-approval/services/useAccessibilityFeatures'
import {useUpdateHostApprovalTaskImage} from 'src/features/admin-host-approval/services/useUpdateHostApprovalTaskImage'
import {useAdminHostApprovalTaskRooms} from 'src/features/admin-host-approval/services/useAdminHostApprovalTaskRooms'
import {raise} from 'src/helpers/helpers'
import {StyledHostApprovalImageAccessibilityTriggerText} from 'src/features/admin-host-approval/components/admin-host-approval-image-accessibility-selector/style'
import {useMemo} from 'react'

export const AdminHostApprovalImageAccessibilitySelector = ({
    taskId,
    imageId,
    amenityId,
    roomId
}: {
    taskId: number
    imageId: number
    amenityId: AdminHostApprovalTaskImage['amenity_id']
    roomId: AdminHostApprovalTaskImage['room_id']
}) => {
    const {t} = useTranslation()
    const {data: accessibilityFeatures, isLoading, isError} = useAccessibilityFeatures({staleTime: Infinity})
    const {data: rooms} = useAdminHostApprovalTaskRooms({staleTime: Infinity})
    const {mutate: updateImage} = useUpdateHostApprovalTaskImage({
        taskId,
        imageId
    })

    const filteredAccessibilityFeatures = useMemo(() => {
        if (!accessibilityFeatures || !rooms || !roomId) return accessibilityFeatures ?? []

        const selectedRoom = rooms.find(room => room.id === roomId)
        if (!selectedRoom) return accessibilityFeatures

        const allowedFeatureKeys = ROOM_ACCESSIBILITY_FEATURES_MAP[selectedRoom.internal_name]
        return accessibilityFeatures.filter(feature => allowedFeatureKeys.includes(feature.internal_key))
    }, [accessibilityFeatures, rooms, roomId])

    return (
        <SelectProvider
            defaultValue={amenityId?.toString() ?? ''}
            setValue={value => updateImage({amenity_id: +value})}
        >
            <Flexbox direction="column" gap={2} fullWidth>
                <AdminHostApprovalImageAccessibilityTrigger />
                <SelectPopover portal sameWidth={false}>
                    {isLoading ? (
                        <Flexbox justify="center">
                            <Spinner size={20} />
                        </Flexbox>
                    ) : isError ? (
                        <Flexbox justify="center">
                            <InputHelpText error={t('errors:default')} />
                        </Flexbox>
                    ) : (
                        <SelectList>
                            {filteredAccessibilityFeatures.map(accessibilityFeature => (
                                <SelectItem key={accessibilityFeature.id} value={accessibilityFeature.id.toString()}>
                                    {t(`admin:amenities:${accessibilityFeature.internal_key.toLowerCase()}`)}
                                </SelectItem>
                            ))}
                        </SelectList>
                    )}
                </SelectPopover>
            </Flexbox>
        </SelectProvider>
    )
}

const AdminHostApprovalImageAccessibilityTrigger = () => {
    const {t} = useTranslation()
    const {data: amenities} = useAccessibilityFeatures({enabled: false})
    const selectContext = useSelectContext() ?? raise('Select must be a child of SelectCombobox')
    const selectValue = selectContext.useState('value') as string //as needed because it's a single select and the provided type is string | string[]

    return (
        <SelectTrigger
            placeholder={t('host_approval_task:select_accessibility_feature_placeholder')}
            fullWidth
            value={selectValue}
            displayValue={value => {
                const amenity = amenities?.find(a => a.id.toString() == value)
                return (
                    <StyledHostApprovalImageAccessibilityTriggerText>
                        {amenity ? t(`admin:amenities:${amenity.internal_key.toLowerCase()}`) : ''}
                    </StyledHostApprovalImageAccessibilityTriggerText>
                )
            }}
        />
    )
}
